import { Box, Stack, useColorModeValue, useBreakpointValue, Center, Text } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import type { DealFinderProps } from "../../types/dealfinder";
import { DealFinderContent } from "./components/DealFinderContent";
import { TabTitle } from "screens/common/components/TabTitle";
import { useCustomScrollbar } from "hooks/useCustomScrollbar";
import { useConversationContext } from "screens/thread/ConversationContext";
import { ActionButtonsWrapper } from "screens/collection/views/AutocompleteSuggestion/ActionButtonsWrapper";
import { ScrollingTickers } from "screens/common/components/ScrollingTickers";
import { registerAnonymousUser } from "state/session/operations";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";
import { TypingIndicator } from "screens/thread/components/cells/components";
import { HEADER_HEIGHT } from "screens/landing";
import { useSidebarNavigation, useUserPreference } from "hooks";
import { useCopyValue } from "hooks/useCopies";
import { ConversationDialogInput } from "screens/thread/ConversationDialogInput";

export const DealFinder: React.FC<DealFinderProps> = () => {
  const bgColor = useColorModeValue("#fbfbfb", "gray.800");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollbarStyle } = useCustomScrollbar(containerRef, { width: "2px", barTransparency: 0.2 });
  const { setShouldClearInputValue } = useConversationContext();
  const filterOptions = ["EQUITY", "ETF", "MUTUAL FUNDS", "PRIVATE"];
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector((state: RootState) => state.session, shallowEqual);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const dealFinderActionButtons = useCopyValue("copy_deal_finder_action_buttons") as "all" | "filter" | "quick_links" | "none";
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { fallback: "md", ssr: false });
  const showFullWidth = useUserPreference("ui_enable_view_full_width") as boolean;
  const { currentSidebarType } = useSidebarNavigation();
  const headerPlaceholder = useCopyValue("copy_header_interaction_bar_placeholder");
  const headerPlaceholderMobile = useCopyValue("copy_header_interaction_bar_placeholder_mobile");
  const marketingTitle = useCopyValue("copy_deal_finder_marketing_title");
  const marketingSubtitle = useCopyValue("copy_deal_finder_marketing_subtitle");
  const { isConversationOpen } = useConversationContext();
  const bannerTextColor = useColorModeValue("white", "gray.800");

  useEffect(() => {
    try {
      if (!isLoggedIn && !isAuthenticating) {
        dispatch(registerAnonymousUser());
      }
    } catch (error) {
      console.error("Anonymous authentication failed:", error);
    } finally {
      setIsAuthenticating(false);
    }
  }, [dispatch, isAuthenticating, isLoggedIn]);

  // Show loading indicator while authenticating
  if (isAuthenticating) {
    return (
      <Box bg={bgColor} fontFamily="Montserrat" overflow="auto" height={"100%"} pb="2rem">
        <TabTitle title={`Deal Finder`} />
        <Center backgroundColor={bgColor} zIndex={100} width="100%" height={`calc(100vh - ${HEADER_HEIGHT - 1}px)`}>
          <TypingIndicator />
        </Center>
      </Box>
    );
  }

  return (
    <Box
      bg={bgColor}
      fontFamily="Montserrat"
      height={"100%"}
      pb="2rem"
      css={scrollbarStyle}
      width="100%"
      mx="auto"
      pl={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" && !showFullWidth ? "0" : "1rem"}
      pr={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" && !showFullWidth ? "0" : "1rem"}>
      <TabTitle title={`Deal Finder`} />
      <Box pb={0}>
        <Stack spacing={"2rem"} alignItems="center" pt={isMobile ? "1rem" : "2rem"}>
          <Center>
            <ScrollingTickers />
          </Center>
          <Box
            position="relative"
            width="100%"
            borderRadius="2xl"
            pb={isTablet ? "2rem" : isMobile ? "1rem" : "6rem"}
            pt={isTablet ? "2rem" : isMobile ? "1rem" : "5rem"}
            textAlign="center"
            _before={{
              content: '""',
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              bottom: "0",
              background: `
                     radial-gradient(circle at 20% 30%, rgba(0, 70, 103, 0.98) 0%, transparent 50%),radial-gradient(circle at 80% 70%, rgb(82, 165, 187) 0%, #00000014 50%),radial-gradient(circle at 60% 20%, rgba(14, 178, 206, 0.5) 0%, #0000 40%),linear-gradient(135deg, rgba(10, 47, 58, 0.54) 0%, rgba(7, 82, 116, 0.82) 50%, rgba(22, 106, 95, 0.2) 100%)
              `,
              borderRadius: "2xl",
              zIndex: 0,
              pointerEvents: "none",
            }}
            _after={{
              content: '""',
              position: "absolute",
              top: "-1rem",
              left: "50%",
              transform: "translateX(-50%)",
              width: "24rem",
              height: "24rem",
              background: "radial-gradient(circle, rgba(1, 79, 115, 0.3) 0%, transparent 70%)",
              borderRadius: "50%",
              zIndex: 1,
              pointerEvents: "none",
            }}>
            <Stack textAlign="center" zIndex={2} position="relative" px={isMobile ? "1rem" : "3rem"}>
              <Text fontSize={isTablet ? "2rem" : isMobile ? "2xl" : "2.5rem"} fontWeight="bold" textAlign="center" color={bannerTextColor}>
                {marketingTitle}
              </Text>
              {!isMobile && (
                <Center>
                  <Text
                    maxWidth={isTablet ? "30rem" : "100%"}
                    fontSize={isTablet ? "1.2rem" : isMobile ? "sm" : "1.3rem"}
                    fontWeight={"semibold"}
                    textAlign="center"
                    color={bannerTextColor}>
                    {marketingSubtitle}
                  </Text>
                </Center>
              )}
              <Center width="100%" pt={isTablet ? "2rem" : isMobile ? "1rem" : "3rem"} pb="1rem">
                <Box maxWidth={isMobile ? "100%" : isTablet ? "30rem" : "40rem"} width="100%" height={"4rem"}>
                  <ConversationDialogInput
                    setListType="deals"
                    minWidth={isMobile ? "100%" : isTablet ? "30rem" : "40rem"}
                    minWidthList={isMobile ? "100%" : isTablet ? "30rem" : "40rem"}
                    showCharliIcon={false}
                    showDealFinderIcon
                    tooltip={undefined}
                    inputId="deal-finder-input"
                    className="ch-autocomplete-input-header"
                    initialText={isMobile || isTablet ? headerPlaceholderMobile : headerPlaceholder}
                    disableComponent={isConversationOpen}
                  />
                </Box>
              </Center>
              {dealFinderActionButtons !== "none" && (
                <ActionButtonsWrapper showButtons={dealFinderActionButtons} size="md" uniqueFilters={filterOptions} />
              )}
            </Stack>
          </Box>
          <DealFinderContent onResetInput={() => setShouldClearInputValue(true)} />
        </Stack>
      </Box>
    </Box>
  );
};

export default DealFinder;
